import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'

import {
  DEFAULT_LOCALE,
  LANGUAGES,
  i18n,
  isSupportedLocale,
  languageFor,
  type SupportedLocale,
} from '@/i18n'

const STORAGE_KEY = 'raldo.locale'

function readStoredLocale(): SupportedLocale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE
  } catch {
    // Intentionally ignored: private-mode storage throws.
    return DEFAULT_LOCALE
  }
}

/** The app's UI locale; the single place a locale change is applied. */
export const useLocaleStore = defineStore('locale', () => {
  const locale = ref<SupportedLocale>(readStoredLocale())

  const language = computed(() => languageFor(locale.value))
  const languages = computed(() => LANGUAGES)

  // vue-i18n narrows its locale type to shipped catalogues; widened because de/it fall back at runtime.
  const activeLocale = i18n.global.locale as unknown as Ref<string>

  function apply(next: SupportedLocale): void {
    locale.value = next
    activeLocale.value = next
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Intentionally ignored: the selection still applies for this session.
    }
  }

  function setLocale(next: string): void {
    if (!isSupportedLocale(next) || next === locale.value) return
    apply(next)
  }

  function init(): void {
    apply(locale.value)
  }

  return { locale, language, languages, setLocale, init }
})
